// ---------------------------------------------------------------------------
// PromptEditor — Dialog to add/edit a prompt in the template tree
// ---------------------------------------------------------------------------

import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { QuestionPickerDialog } from './QuestionPickerDialog';
import { InfoTextPickerDialog } from './InfoTextPickerDialog';
import type { TemplatePrompt, AlertLevel, PromptAnswerInput } from '@/types/template';
import type { Question, InformationalText } from '@/types/question';

interface PromptEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prompt?: TemplatePrompt;
  sessionNumber: number;
  existingPrompts: TemplatePrompt[];
  /** Readable labels keyed by prompt_id (e.g. "#1 — Have you had..."). */
  promptLabels?: Map<string, string>;
  /** When set, the editor is in "nested add" mode — routing is automatic. */
  nestLabel?: string;
  /** Override the default sort order for new prompts. */
  suggestedSortOrder?: number;
  onSave: (data: {
    isInformational: boolean;
    questionId?: number;
    informationalTextId?: number;
    promptText?: string;
    sortOrder: number;
    sessionNumber: number;
    answers: PromptAnswerInput[];
  }) => void;
  isPending: boolean;
}

export function PromptEditor({
  open,
  onOpenChange,
  prompt,
  sessionNumber,
  existingPrompts,
  promptLabels,
  nestLabel,
  suggestedSortOrder,
  onSave,
  isPending,
}: PromptEditorProps) {
  const isEdit = !!prompt;

  const [isInformational, setIsInformational] = useState(prompt?.is_informational ?? false);
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
  const [questionId, setQuestionId] = useState<number | undefined>(prompt?.question_id ?? undefined);
  const [promptText, setPromptText] = useState(prompt?.prompt_text ?? '');
  const [informationalTextId, setInformationalTextId] = useState<number | undefined>(
    (prompt as TemplatePrompt & { informational_text_id?: number })?.informational_text_id ?? undefined,
  );
  const [infoPickerOpen, setInfoPickerOpen] = useState(false);
  // Auto-calculate next sort order for new prompts so the unique constraint
  // (template_id, session_number, sort_order) isn't violated.
  const sortOrder = prompt?.sort_order
    ?? suggestedSortOrder
    ?? Math.max(0, ...existingPrompts
        .filter((p) => p.session_number === sessionNumber)
        .map((p) => p.sort_order)) + 1;
  const [session, setSession] = useState(prompt?.session_number ?? sessionNumber);
  const [answers, setAnswers] = useState<PromptAnswerInput[]>(
    prompt?.answers.map((a) => ({
      answerText: a.answer_text,
      alertLevel: a.alert_level,
      keypress: a.keypress ?? undefined,
      nextPromptId: a.next_prompt_id ?? undefined,
      sortOrder: a.sort_order,
    })) ?? [],
  );
  const [pickerOpen, setPickerOpen] = useState(false);

  function addAnswer() {
    setAnswers((prev) => [
      ...prev,
      { answerText: '', alertLevel: 0, sortOrder: prev.length + 1 },
    ]);
  }

  function removeAnswer(idx: number) {
    setAnswers((prev) => prev.filter((_, i) => i !== idx));
  }

  function handleSubmit() {
    onSave({
      isInformational,
      questionId: isInformational ? undefined : questionId,
      informationalTextId: isInformational ? informationalTextId : undefined,
      promptText: isInformational ? promptText : undefined,
      sortOrder,
      sessionNumber: session,
      answers,
    });
  }

  function handleInfoTextSelected(item: InformationalText) {
    setInformationalTextId(item.informational_text_id);
    setPromptText(item.body);
  }

  function handleQuestionSelected(q: Question) {
    setSelectedQuestion(q);
    setQuestionId(q.question_id);
    // Pre-fill answers from question if empty. Note: list-view payloads
    // don't include answers — only the detail endpoint does, so this
    // path may receive q.answers undefined.
    if (answers.length === 0 && (q.answers?.length ?? 0) > 0) {
      setAnswers(
        (q.answers ?? []).map((a) => ({
          answerText: a.answer_text,
          alertLevel: a.alert_level,
          sortOrder: a.display_order,
        })),
      );
    }
  }

  const questionLabel = selectedQuestion
    ? selectedQuestion.question_text
    : questionId
      ? `Question #${questionId}`
      : null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEdit ? 'Edit Prompt' : 'Add Prompt'}</DialogTitle>
            <DialogDescription>
              {isEdit ? 'Update this prompt and its answers.' : 'Add a new prompt to the session.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {/* Nesting context banner */}
            {nestLabel && (
              <div className="rounded-ds-004 border border-purple-200 bg-purple-50 px-3 py-2 text-xs text-purple-700">
                Nesting under {nestLabel} — routing will be set automatically.
              </div>
            )}

            {/* Type toggle */}
            <div className="flex gap-2">
              <Button
                type="button"
                variant={isInformational ? 'default' : 'secondary'}
                size="sm"
                onClick={() => setIsInformational(true)}
              >
                Informational
              </Button>
              <Button
                type="button"
                variant={!isInformational ? 'default' : 'secondary'}
                size="sm"
                onClick={() => setIsInformational(false)}
              >
                Question-based
              </Button>
            </div>

            {/* Content based on type */}
            {isInformational ? (
              <div>
                <Label className="mb-1">Informational Text</Label>
                <div className="flex items-start gap-2">
                  {promptText ? (
                    <p className="flex-1 text-sm text-carbon-800 bg-silver-50 rounded-ds-004 px-3 py-2 border border-silver-200 whitespace-pre-wrap">
                      {promptText}
                    </p>
                  ) : (
                    <p className="flex-1 text-sm text-carbon-400 italic">No text selected</p>
                  )}
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => setInfoPickerOpen(true)}
                  >
                    Pick Text
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <Label className="mb-1">Question</Label>
                <div className="flex items-center gap-2">
                  {questionLabel ? (
                    <p className="flex-1 text-sm text-carbon-800 bg-silver-50 rounded-ds-004 px-3 py-2 border border-silver-200">
                      {questionLabel}
                    </p>
                  ) : (
                    <p className="flex-1 text-sm text-carbon-400 italic">No question selected</p>
                  )}
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => setPickerOpen(true)}
                  >
                    Pick Question
                  </Button>
                </div>
              </div>
            )}

            {/* Session */}
            <div>
              <Label htmlFor="session" className="mb-1">Session</Label>
              <Input
                id="session"
                type="number"
                value={session}
                onChange={(e) => setSession(Number(e.target.value))}
                min={1}
              />
            </div>

            {/* Answers */}
            {!isInformational && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Answers</Label>
                  <Button type="button" variant="ghost" size="sm" onClick={addAnswer}>
                    <Plus className="h-3 w-3 mr-1" /> Add Answer
                  </Button>
                </div>
                {/* Column headers */}
                {answers.length > 0 && (
                  <div className="flex items-center gap-2 mb-1">
                    <span className="flex-1 text-xs font-medium text-carbon-500">Answer Text</span>
                    <span className="w-20 text-xs font-medium text-carbon-500">Alert</span>
                    {!nestLabel && (
                      <span className="w-48 text-xs font-medium text-carbon-500">Next Prompt</span>
                    )}
                    <span className="w-8" />
                  </div>
                )}
                <div className="space-y-2">
                  {answers.map((a, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Input
                        placeholder={`Answer ${idx + 1}`}
                        value={a.answerText}
                        onChange={(e) => {
                          const next = [...answers];
                          next[idx] = { ...next[idx], answerText: e.target.value };
                          setAnswers(next);
                        }}
                        className="flex-1"
                      />
                      <Select
                        value={String(a.alertLevel ?? 0)}
                        onValueChange={(v) => {
                          const next = [...answers];
                          next[idx] = { ...next[idx], alertLevel: Number(v) as AlertLevel };
                          setAnswers(next);
                        }}
                      >
                        <SelectTrigger className="w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {([0, 1, 2, 3, 4] as AlertLevel[]).map((l) => (
                            <SelectItem key={l} value={String(l)}>L{l}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {!nestLabel && (
                        <Select
                          value={a.nextPromptId ?? '__none__'}
                          onValueChange={(v) => {
                            const next = [...answers];
                            next[idx] = {
                              ...next[idx],
                              nextPromptId: v === '__none__' ? undefined : v,
                            };
                            setAnswers(next);
                          }}
                        >
                          <SelectTrigger className="w-48">
                            <SelectValue placeholder="Next..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">None</SelectItem>
                            {existingPrompts
                              .filter((p) => {
                                if (prompt && p.prompt_id === prompt.prompt_id) return false;
                                if (p.session_number === session) return p.sort_order > sortOrder;
                                return p.session_number > session;
                              })
                              .map((p) => (
                              <SelectItem key={p.prompt_id} value={p.prompt_id}>
                                {promptLabels?.get(p.prompt_id) ?? `#${p.sort_order} S${p.session_number}`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="shrink-0 h-8 w-8 text-red-500 hover:text-red-700"
                        onClick={() => removeAnswer(idx)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="mt-4">
            <Button variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isPending || (isInformational ? !promptText : !questionId)}
            >
              {isPending ? 'Saving...' : isEdit ? 'Update Prompt' : 'Add Prompt'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {pickerOpen && (
        <QuestionPickerDialog
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          onSelect={handleQuestionSelected}
        />
      )}

      {infoPickerOpen && (
        <InfoTextPickerDialog
          open={infoPickerOpen}
          onOpenChange={setInfoPickerOpen}
          onSelect={handleInfoTextSelected}
        />
      )}
    </>
  );
}
